import { useState, useEffect, useRef, useCallback } from 'react';
import { getWalletBalance, updateWalletAfterOrder, calcApplicableBonus } from '@/hooks/useWallet';
import { saveOrder, generateOTP, saveDeliveryOTP, supabase } from '@/lib/supabase';
import { MetaEvents, trackWithAttribution } from '@/lib/metaPixel';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import About from './components/About';
import Services from './components/Services';
import Portfolio, { type PortfolioRef } from './components/Portfolio';
import HotCollection from './components/HotCollection';
import CategoryChips from './components/CategoryChips';
import NewArrivals from './components/NewArrivals';
import TodaysDeals from './components/TodaysDeals';
import Contact from './components/Contact';
import Footer from './components/Footer';
import OrderTrackingWidget from './components/OrderTrackingWidget';
import VideoPopup from '@/components/feature/VideoPopup';
import BottomNav from '@/components/feature/BottomNav';
import { usePageTracking } from '@/hooks/useVisitorTracking';

export interface CartItem {
  id: number;
  name: string;
  price: number;
  originalPrice: number;
  image: string;
  category: string;
  quantity: number;
}

interface Address {
  fullName: string;
  phone: string;
  address: string;
  city: string;
  pincode: string;
  lat?: number;
  lng?: number;
  mapAddress?: string;
}

interface HomeSection {
  id: string;
  enabled: boolean;
  order: number;
}

const useScrollToTop = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);
};

// Default sections order
const DEFAULT_SECTIONS: HomeSection[] = [
  { id: 'hero', enabled: true, order: 1 },
  { id: 'categories', enabled: true, order: 2 },
  { id: 'hotcollection', enabled: true, order: 3 },
  { id: 'deals', enabled: true, order: 4 },
  { id: 'newarrivals', enabled: true, order: 5 },
  { id: 'portfolio', enabled: true, order: 6 },
  { id: 'about', enabled: true, order: 7 },
  { id: 'services', enabled: true, order: 8 },
  { id: 'contact', enabled: true, order: 9 },
];

export default function Home() {
  useScrollToTop();
  usePageTracking('home');
  const [scrolled, setScrolled] = useState(false);
  const portfolioRef = useRef<PortfolioRef>(null);

  // ── Home Layout State ──
  const [sections, setSections] = useState<HomeSection[]>(DEFAULT_SECTIONS);
  const [layoutLoading, setLayoutLoading] = useState(true);

  // ── Location Gate ── (disabled - popup removed)
  const [locationChecked] = useState(true);
  const [inServiceArea] = useState(true);

  // ── Cart State ──
  const [cart, setCart] = useState<CartItem[]>(() => {
    try {
      const saved = localStorage.getItem('clothybazar_cart');
      return saved ? (JSON.parse(saved) as CartItem[]) : [];
    } catch { return []; }
  });
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [showToast, setShowToast] = useState(false);

  // ── Wallet ──
  const [walletBalance, setWalletBalance] = useState(0);
  const [applyBonus, setApplyBonus] = useState(false);

  // ── Address ──
  const [savedAddress, setSavedAddress] = useState<Address | null>(() => {
    try {
      const s = localStorage.getItem('userAddress');
      return s ? JSON.parse(s) : null;
    } catch { return null; }
  });
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [addressForm, setAddressForm] = useState<Address>({
    fullName: '', phone: '', address: '', city: 'Dehradun', pincode: '',
  });
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationError, setLocationError] = useState('');
  const [mapCoords, setMapCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [locationDetected, setLocationDetected] = useState(false);

  // ── Order Confirm Countdown ──
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [countdown, setCountdown] = useState(5);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Load home layout from Supabase
  useEffect(() => {
    const loadLayout = async () => {
      const { data } = await supabase
        .from('home_layout')
        .select('sections')
        .eq('id', 'main')
        .maybeSingle();
      
      if (data?.sections) {
        setSections(data.sections as HomeSection[]);
      }
      setLayoutLoading(false);
    };
    loadLayout();

    // Subscribe to real-time changes
    const channel = supabase
      .channel('home_layout_changes')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'home_layout', filter: 'id=eq.main' },
        (payload) => {
          if (payload.new && (payload.new as { sections: HomeSection[] }).sections) {
            setSections((payload.new as { sections: HomeSection[] }).sections);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  // Save cart to localStorage + track AddToCart
  useEffect(() => {
    localStorage.setItem('clothybazar_cart', JSON.stringify(cart));
  }, [cart]);

  // Refresh wallet when cart opens
  useEffect(() => {
    if (isCartOpen) { setWalletBalance(getWalletBalance()); setApplyBonus(false); }
  }, [isCartOpen]);

  // Load saved address
  useEffect(() => {
    const s = localStorage.getItem('userAddress');
    if (s) {
      const parsed: Address = JSON.parse(s);
      setSavedAddress(parsed);
      if (parsed.lat && parsed.lng) setMapCoords({ lat: parsed.lat, lng: parsed.lng });
    }
  }, []);

  const cartTotal  = cart.reduce((s, i) => s + i.price * i.quantity, 0);
  const cartMRP    = cart.reduce((s, i) => s + i.originalPrice * i.quantity, 0);
  const cartSaved  = cartMRP - cartTotal;
  const cartCount  = cart.reduce((s, i) => s + i.quantity, 0);
  const bonusApplied = applyBonus ? calcApplicableBonus(walletBalance, cartTotal) : 0;
  const finalTotal   = cartTotal - bonusApplied;
  const willEarn     = Math.floor(finalTotal * 0.1);

  const clearCountdown = () => {
    if (countdownRef.current) { clearInterval(countdownRef.current); countdownRef.current = null; }
  };

  // ── Execute Order (saves to Supabase) ──
  const executeOrder = useCallback(async () => {
    if (!savedAddress || submitting) return;
    setSubmitting(true);
    const orderId = 'ORD-' + Date.now();
    const bApplied = applyBonus ? calcApplicableBonus(walletBalance, cartTotal) : 0;
    const fTotal   = cartTotal - bApplied;
    const earned   = updateWalletAfterOrder(fTotal, bApplied);
    const otp      = generateOTP();

    // Track Purchase event for Meta Ads
    const contentIds = cart.map(item => String(item.id));
    trackWithAttribution('Purchase', {
      content_ids: contentIds,
      content_type: 'product',
      value: fTotal,
      currency: 'INR',
      order_id: orderId,
      num_items: cart.length,
    });

    await saveOrder({
      id: orderId,
      customer_name: savedAddress.fullName,
      customer_phone: savedAddress.phone,
      customer_address: savedAddress.address,
      customer_city: savedAddress.city,
      customer_pincode: savedAddress.pincode,
      customer_lat: savedAddress.lat,
      customer_lng: savedAddress.lng,
      items: cart,
      total_amount: cartTotal,
      wallet_discount: bApplied,
      final_amount: fTotal,
      payment_method: 'COD',
      status: 'pending',
      order_type: 'cart',
    });
    await saveDeliveryOTP(orderId, otp);

    const orderData = {
      id: orderId, items: cart, address: savedAddress,
      paymentMethod: 'Cash on Delivery', total: fTotal,
      orderDate: new Date().toISOString(), status: 'confirmed',
    };
    localStorage.setItem('currentOrder', JSON.stringify(orderData));

    setCart([]);
    setIsCartOpen(false);
    setShowConfirmModal(false);
    setSubmitting(false);
    setToastMessage(`Order placed! Earned ₹${earned} bonus for next order`);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3500);
  }, [savedAddress, submitting, applyBonus, walletBalance, cartTotal, cart]);

  const executeOrderRef = useRef(executeOrder);
  useEffect(() => { executeOrderRef.current = executeOrder; }, [executeOrder]);

  const startConfirmation = () => {
    // Track InitiateCheckout for Meta Ads
    const contentIds = cart.map(item => String(item.id));
    trackWithAttribution('InitiateCheckout', {
      content_ids: contentIds,
      content_type: 'product',
      value: finalTotal,
      currency: 'INR',
      num_items: cart.length,
    });

    setCountdown(5);
    setShowConfirmModal(true);
    clearCountdown();
    let remaining = 5;
    countdownRef.current = setInterval(() => {
      remaining -= 1;
      setCountdown(remaining);
      if (remaining <= 0) { clearCountdown(); executeOrderRef.current(); }
    }, 1000);
  };

  useEffect(() => () => clearCountdown(), []);

  const handleCheckout = () => {
    if (cart.length === 0) return;
    if (!inServiceArea) {
      setToastMessage('Sorry! Abhi aapke area mein delivery nahi hai.');
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
      return;
    }
    if (!savedAddress) { setIsCartOpen(false); setShowAddressModal(true); return; }
    setIsCartOpen(false);
    startConfirmation();
  };

  const handleAddressSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const finalAddress = { ...addressForm };
    if (mapCoords) { finalAddress.lat = mapCoords.lat; finalAddress.lng = mapCoords.lng; }
    localStorage.setItem('userAddress', JSON.stringify(finalAddress));
    setSavedAddress(finalAddress);
    setShowAddressModal(false);
    setIsCartOpen(true);
  };

  const handleDetectLocation = () => {
    setLocationError('');
    if (!navigator.geolocation) { setLocationError('Geolocation not supported.'); return; }
    setLocationLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude: lat, longitude: lng } = pos.coords;
        setMapCoords({ lat, lng });
        setLocationDetected(true);
        setAddressForm(prev => ({ ...prev, lat, lng }));
        try {
          const res = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`);
          const data = await res.json();
          const city = data.address?.city || data.address?.town || data.address?.village || 'Dehradun';
          const pincode = data.address?.postcode || '';
          setAddressForm(prev => ({
            ...prev,
            address: data.address?.road ? `${data.address?.road}${data.address?.suburb ? ', ' + data.address?.suburb : ''}` : prev.address,
            city, pincode: pincode || prev.pincode,
            mapAddress: data.display_name || '', lat, lng,
          }));
        } catch { /* ignore */ }
        setLocationLoading(false);
      },
      (err) => {
        setLocationLoading(false);
        setLocationError(err.code === 1 ? 'Location permission denied.' : 'Unable to detect location.');
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const addToCart = (product: Omit<CartItem, 'quantity'>) => {
    setCart(prev => {
      const exists = prev.find(i => i.id === product.id);
      if (exists) return prev.map(i => i.id === product.id ? { ...i, quantity: i.quantity + 1 } : i);
      return [...prev, { ...product, quantity: 1 }];
    });

    // Track AddToCart for Meta Ads
    MetaEvents.addToCart(
      product.name,
      'product',
      [String(product.id)],
      product.price,
      'INR'
    );

    setToastMessage(`${product.name} added to cart!`);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 2000);
  };

  const removeFromCart = (id: number) => setCart(prev => prev.filter(i => i.id !== id));
  const updateQuantity = (id: number, delta: number) =>
    setCart(prev => prev.map(i => i.id === id ? { ...i, quantity: Math.max(1, i.quantity + delta) } : i));

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleShopNowClick = () => { portfolioRef.current?.filterByCategory('Track Pants'); };

  // Render section based on ID
  const renderSection = (sectionId: string) => {
    switch (sectionId) {
      case 'hero':
        return <Hero onShopNowClick={handleShopNowClick} cart={cart} onAddToCart={addToCart} onOpenCart={() => setIsCartOpen(true)} />;
      case 'categories':
        return <CategoryChips />;
      case 'hotcollection':
        return <HotCollection onAddToCart={addToCart} />;
      case 'deals':
        return <TodaysDeals />;
      case 'newarrivals':
        return <NewArrivals onAddToCart={addToCart} />;
      case 'portfolio':
        return <Portfolio ref={portfolioRef} onAddToCart={addToCart} onOpenCart={() => setIsCartOpen(true)} />;
      case 'about':
        return <About />;
      case 'services':
        return <Services />;
      case 'contact':
        return <Contact />;
      default:
        return null;
    }
  };

  // Get sorted enabled sections
  const sortedSections = sections
    .filter(s => s.enabled)
    .sort((a, b) => a.order - b.order);

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950 text-gray-900 dark:text-slate-100 overflow-x-hidden">
      <Navbar scrolled={scrolled} />

      {/* Toast */}
      {showToast && (
        <div className="fixed top-24 right-4 z-[60] animate-slide-in">
          <div className="bg-white border border-gray-200 shadow-xl px-6 py-3 rounded-full flex items-center gap-2">
            <span className="w-6 h-6 flex items-center justify-center rounded-full bg-gradient-primary flex-shrink-0">
              <i className="ri-check-line text-white text-sm"></i>
            </span>
            <span className="text-sm font-semibold text-gray-900">{toastMessage}</span>
          </div>
        </div>
      )}

      {/* ── Address Modal ── */}
      {showAddressModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl w-full max-w-lg p-6 shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900">Delivery Address</h3>
              <button onClick={() => { setShowAddressModal(false); setIsCartOpen(true); }} className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center cursor-pointer hover:bg-gray-200">
                <i className="ri-close-line text-gray-600"></i>
              </button>
            </div>

            {/* Map */}
            <div className="mb-5">
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-semibold text-gray-700 flex items-center gap-1">
                  <i className="ri-map-pin-2-fill text-red-500"></i>Location on Map
                </label>
                <button type="button" onClick={handleDetectLocation} disabled={locationLoading}
                  className="flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-green-500 to-emerald-600 text-white text-xs font-semibold disabled:opacity-60 cursor-pointer whitespace-nowrap">
                  {locationLoading ? <><i className="ri-loader-4-line animate-spin"></i>Detecting...</> : <><i className="ri-crosshair-2-line"></i>Use My Location</>}
                </button>
              </div>
              {locationError && (
                <div className="mb-2 p-3 rounded-xl bg-red-50 border border-red-200 flex items-start gap-2">
                  <i className="ri-error-warning-line text-red-500 mt-0.5 shrink-0"></i>
                  <p className="text-red-600 text-xs">{locationError}</p>
                </div>
              )}
              <div className="relative w-full rounded-xl overflow-hidden border border-gray-200" style={{ height: '200px' }}>
                {mapCoords ? (
                  <>
                    <iframe title="Map" width="100%" height="200" style={{ border: 0 }} loading="lazy"
                      src={`https://maps.google.com/maps?q=${mapCoords.lat},${mapCoords.lng}&z=16&output=embed`}></iframe>
                    {locationDetected && (
                      <div className="absolute top-2 left-2 bg-green-500 text-white text-xs px-3 py-1 rounded-full font-semibold flex items-center gap-1">
                        <i className="ri-map-pin-fill"></i>Location Pinned
                      </div>
                    )}
                  </>
                ) : (
                  <div className="w-full h-full bg-gray-100 flex flex-col items-center justify-center gap-3">
                    <i className="ri-map-2-line text-3xl text-gray-400"></i>
                    <p className="text-gray-500 text-sm text-center px-4">Click <span className="font-semibold text-green-600">"Use My Location"</span> to pin your delivery spot</p>
                  </div>
                )}
              </div>
            </div>

            <form onSubmit={handleAddressSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                <input type="text" required value={addressForm.fullName}
                  onChange={e => setAddressForm({ ...addressForm, fullName: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-green-500 outline-none text-sm"
                  placeholder="Enter your full name" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                <input type="tel" required value={addressForm.phone}
                  onChange={e => setAddressForm({ ...addressForm, phone: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-green-500 outline-none text-sm"
                  placeholder="+91 98765 43210" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Complete Address</label>
                <textarea required value={addressForm.address}
                  onChange={e => setAddressForm({ ...addressForm, address: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-green-500 outline-none resize-none text-sm"
                  rows={2} placeholder="House no., Street, Landmark" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                  <input type="text" value={addressForm.city}
                    onChange={e => setAddressForm({ ...addressForm, city: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-green-500 outline-none text-sm" placeholder="City" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Pincode</label>
                  <input type="text" required value={addressForm.pincode}
                    onChange={e => setAddressForm({ ...addressForm, pincode: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-green-500 outline-none text-sm" placeholder="248001" />
                </div>
              </div>
              <button type="submit"
                className="w-full py-4 rounded-xl bg-gradient-to-r from-green-500 to-emerald-600 text-white font-semibold flex items-center justify-center gap-2 cursor-pointer">
                <i className="ri-map-pin-add-fill"></i>Save Address & Continue
              </button>
            </form>
          </div>
        </div>
      )}

      {/* ── Order Confirm Countdown Modal ── */}
      {showConfirmModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-3xl w-full max-w-sm p-7 shadow-2xl text-center relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-green-50 to-emerald-100 origin-bottom"
              style={{ transform: `scaleY(${countdown / 5})`, transformOrigin: 'bottom', transition: 'transform 1s linear' }}></div>
            <div className="relative z-10">
              <div className="relative w-24 h-24 mx-auto mb-5">
                <svg className="w-24 h-24 -rotate-90" viewBox="0 0 96 96">
                  <circle cx="48" cy="48" r="42" fill="none" stroke="#e5e7eb" strokeWidth="6" />
                  <circle cx="48" cy="48" r="42" fill="none" stroke="#10b981" strokeWidth="6"
                    strokeLinecap="round"
                    strokeDasharray={`${2 * Math.PI * 42}`}
                    strokeDashoffset={`${2 * Math.PI * 42 * (1 - countdown / 5)}`}
                    style={{ transition: 'stroke-dashoffset 1s linear' }} />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-3xl font-bold text-gray-900 leading-none">{countdown}</span>
                  <span className="text-xs text-gray-500 mt-0.5">sec</span>
                </div>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-1">Placing Your Order...</h3>
              <p className="text-gray-500 text-sm mb-1">
                <span className="font-semibold text-gray-700">{cartCount} item{cartCount > 1 ? 's' : ''}</span> · ₹{finalTotal} (COD)
              </p>
              <p className="text-gray-400 text-xs mb-4">
                Delivering to <span className="font-semibold text-gray-600">{savedAddress?.fullName}</span>
              </p>
              {savedAddress && (
                <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-xl px-3 py-2 mb-5 text-left">
                  <i className="ri-map-pin-fill text-green-600 shrink-0"></i>
                  <p className="text-green-700 text-xs leading-tight truncate">{savedAddress.address}, {savedAddress.city}</p>
                </div>
              )}
              <button
                onClick={() => { clearCountdown(); executeOrderRef.current(); }}
                disabled={submitting}
                className="w-full py-3.5 rounded-2xl bg-gradient-to-r from-green-500 to-emerald-600 text-white font-bold text-sm flex items-center justify-center gap-2 mb-3 cursor-pointer disabled:opacity-60">
                <i className="ri-check-double-line text-lg"></i>
                {submitting ? 'Placing...' : 'Confirm Order Now'}
              </button>
              <button
                onClick={() => { clearCountdown(); setShowConfirmModal(false); setCountdown(5); }}
                className="w-full py-3.5 rounded-2xl border-2 border-red-400 text-red-500 font-bold text-sm flex items-center justify-center gap-2 cursor-pointer">
                <i className="ri-close-circle-line text-lg"></i>Cancel Order
              </button>
              <p className="text-gray-400 text-xs mt-3">Auto-confirms in <span className="font-semibold text-gray-600">{countdown}s</span></p>
            </div>
          </div>
        </div>
      )}

      {/* ── Cart Sidebar ── */}
      {isCartOpen && (
        <div className="fixed inset-0 z-[55] flex justify-end">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setIsCartOpen(false)}></div>
          <div className="relative w-full max-w-md bg-white h-full overflow-y-auto animate-slide-in-right border-l border-gray-200">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-display font-bold text-gray-900">Shopping Cart ({cartCount})</h3>
                <button onClick={() => setIsCartOpen(false)} className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 hover:text-gray-900 transition-colors cursor-pointer">
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>

              {cart.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                    <i className="ri-shopping-bag-line text-3xl text-gray-400"></i>
                  </div>
                  <p className="text-gray-500">Your cart is empty</p>
                  <button onClick={() => setIsCartOpen(false)} className="mt-4 px-6 py-2 rounded-full bg-gradient-primary text-white text-sm font-medium cursor-pointer">
                    Continue Shopping
                  </button>
                </div>
              ) : (
                <>
                  <div className="space-y-4 mb-6">
                    {cart.map(item => (
                      <div key={item.id} className="flex gap-4 p-4 rounded-xl bg-gray-50 border border-gray-200">
                        <img src={item.image} alt={item.name} className="w-20 h-24 object-cover rounded-lg flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <h4 className="text-gray-900 font-medium text-sm leading-tight">{item.name}</h4>
                          <p className="text-gray-500 text-xs">{item.category}</p>
                          <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                            <span className="text-primary-pink font-bold text-base">₹{item.price}</span>
                            <span className="text-gray-500 text-xs line-through">₹{item.originalPrice}</span>
                            <span className="text-xs text-green-700 font-semibold">{Math.round((1 - item.price / item.originalPrice) * 100)}% off</span>
                          </div>
                          <div className="flex items-center gap-3 mt-2">
                            <button onClick={() => updateQuantity(item.id, -1)} className="w-7 h-7 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 hover:text-gray-900 cursor-pointer"><i className="ri-subtract-line text-xs"></i></button>
                            <span className="text-gray-900 text-sm font-medium">{item.quantity}</span>
                            <button onClick={() => updateQuantity(item.id, 1)} className="w-7 h-7 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 hover:text-gray-900 cursor-pointer"><i className="ri-add-line text-xs"></i></button>
                            <button onClick={() => removeFromCart(item.id)} className="ml-auto text-gray-500 hover:text-red-400 transition-colors cursor-pointer"><i className="ri-delete-bin-line"></i></button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Delivery Address in Cart */}
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-semibold text-gray-800 flex items-center gap-1">
                        <i className="ri-map-pin-2-fill text-red-500"></i>Delivery Address
                      </span>
                      <button onClick={() => { setIsCartOpen(false); setShowAddressModal(true); }}
                        className="text-xs text-green-700 font-semibold hover:underline cursor-pointer">
                        {savedAddress ? 'Change' : 'Add Address'}
                      </button>
                    </div>
                    {savedAddress ? (
                      <div className="p-3 rounded-xl bg-green-50 border border-green-200">
                        <p className="text-gray-900 text-sm font-semibold">{savedAddress.fullName}</p>
                        <p className="text-gray-600 text-xs leading-relaxed">{savedAddress.address}</p>
                        <p className="text-gray-600 text-xs">{savedAddress.city}{savedAddress.pincode ? ` - ${savedAddress.pincode}` : ''}</p>
                        <p className="text-gray-500 text-xs mt-0.5"><i className="ri-phone-line mr-1"></i>{savedAddress.phone}</p>
                      </div>
                    ) : (
                      <button onClick={() => { setIsCartOpen(false); setShowAddressModal(true); }}
                        className="w-full p-3 rounded-xl border-2 border-dashed border-gray-300 flex items-center justify-center gap-2 text-gray-500 hover:border-green-500 hover:text-green-600 transition-all cursor-pointer">
                        <i className="ri-map-pin-add-line text-lg"></i>
                        <span className="text-sm font-medium">Add Delivery Address</span>
                      </button>
                    )}
                  </div>

                  <div className="border-t border-gray-200 pt-4 space-y-3">
                    {/* Wallet */}
                    <div className={`rounded-2xl p-4 border transition-all ${walletBalance > 0 ? 'bg-amber-500/10 border-amber-500/25' : 'bg-white/5 border-white/10'}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-amber-500/20">
                            <i className="ri-wallet-3-fill text-amber-400 text-base"></i>
                          </div>
                          <div>
                            <p className="text-gray-900 text-sm font-semibold">Wallet Bonus</p>
                            {walletBalance > 0
                              ? <p className="text-amber-400 text-xs">₹{walletBalance} available</p>
                              : <p className="text-gray-500 text-xs">No balance yet</p>}
                          </div>
                        </div>
                        {walletBalance > 0 && (
                          <button onClick={() => setApplyBonus(p => !p)}
                            className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${applyBonus ? 'bg-green-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                            {applyBonus ? <><i className="ri-check-line mr-1"></i>Applied</> : 'Apply'}
                          </button>
                        )}
                      </div>
                      {applyBonus && bonusApplied > 0 && (
                        <div className="mt-2 flex items-center gap-1.5 text-green-400 text-xs">
                          <i className="ri-checkbox-circle-fill"></i>
                          <span>₹{bonusApplied} bonus discount applied!</span>
                        </div>
                      )}
                    </div>

                    {/* Price breakdown */}
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-500 text-sm">MRP Total</span>
                        <span className="text-gray-400 text-sm line-through">₹{cartMRP}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-500 text-sm">Product Discount</span>
                        <span className="text-green-600 text-sm font-semibold">- ₹{cartSaved}</span>
                      </div>
                      {applyBonus && bonusApplied > 0 && (
                        <div className="flex items-center justify-between">
                          <span className="text-amber-600 text-sm">Wallet Bonus</span>
                          <span className="text-amber-600 text-sm font-semibold">- ₹{bonusApplied}</span>
                        </div>
                      )}
                      <div className="flex items-center justify-between">
                        <span className="text-gray-500 text-sm">Delivery</span>
                        <span className="text-green-600 text-sm font-semibold">FREE</span>
                      </div>
                    </div>

                    <div className="border-t border-gray-200 pt-3 flex items-center justify-between">
                      <span className="text-gray-900 font-semibold">Total Amount</span>
                      <span className="text-gray-900 font-bold text-xl">₹{finalTotal}</span>
                    </div>

                    <div className="flex items-center justify-center gap-2 bg-green-500/10 border border-green-500/25 rounded-2xl py-2.5 px-4">
                      <i className="ri-price-tag-3-fill text-green-400"></i>
                      <span className="text-green-700 text-sm font-bold">You saved ₹{cartSaved + bonusApplied} on this order!</span>
                    </div>

                    <div className="flex items-center gap-2 bg-amber-500/10 border border-amber-500/20 rounded-xl px-4 py-2.5">
                      <i className="ri-gift-2-fill text-amber-400 text-sm"></i>
                      <p className="text-amber-700 text-xs">Earn <span className="font-bold text-amber-800">₹{willEarn}</span> bonus on this order!</p>
                    </div>

                    <button onClick={handleCheckout}
                      className="w-full py-4 rounded-full bg-gradient-to-r from-green-500 to-emerald-600 text-white font-bold text-base hover:shadow-lg hover:shadow-green-500/30 transition-all cursor-pointer flex items-center justify-center gap-2 whitespace-nowrap">
                      <i className="ri-truck-line text-lg"></i>
                      Order Now - COD · ₹{finalTotal}
                    </button>
                    <p className="text-center text-gray-500 text-xs mt-1">Cash on Delivery · Pay when order arrives</p>
                    <button onClick={() => setIsCartOpen(false)} className="w-full py-3 rounded-full border border-gray-300 text-gray-700 text-sm hover:bg-gray-50 transition-all cursor-pointer">
                      Continue Shopping
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Floating Cart Button */}
      <button onClick={() => setIsCartOpen(true)}
        className="fixed top-24 right-4 z-40 w-12 h-12 rounded-full bg-gradient-primary flex items-center justify-center text-white shadow-lg hover:scale-110 transition-transform cursor-pointer">
        <i className="ri-shopping-bag-line text-xl"></i>
        {cartCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center font-bold">{cartCount}</span>
        )}
      </button>

      <main>
        {sortedSections.map(section => (
          <div key={section.id}>
            {renderSection(section.id)}
          </div>
        ))}
      </main>
      <Footer />
      <OrderTrackingWidget />
      <VideoPopup />
      <BottomNav cartCount={cartCount} onOpenCart={() => setIsCartOpen(true)} />
    </div>
  );
}
